import {
	ContentWithSidebar,
	Dialog,
	FeaturedBanner,
	Toolbar,
	useAlertDialog,
	FormattedText,
	Space
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback
} from "client/Hooks";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import {
	AppModel,
	getEditAppModel,
	getNewAppFromMainModel,
	getNewEnvironmentAppModel
} from "client/Api/Apps/AppStore/appStore";
import { AppType } from "client/Api/Apps/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { HttpRequestError } from "client/Api/httpClient";
import { isAppInBeta } from "client/Features/Workspace/appTypeHelper";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { AppLinkDialog } from "../../AppLink/AppLinkDialog";
import { EditAppModel, EditGenericAppViewModel } from "client/Api/Apps/EditApp/saveApp";
import { EditApp, EditAppHandle } from "./EditApp";
import { EditAppSettings } from "./EditAppBase";
import { EditAppSavedResult } from "./useSaveApp";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";

export type EditAppDialogContentProps = {
	target: AppTarget;
	activateWhoHasAccessTab: boolean;
	onAfterSave: (id: number, type: AppType, name: string) => void;
	onBack: () => void;
	onClose?: () => void;
	onIsBusyChanged: (value: boolean) => void;
};

export const EditAppDialogContent = (props: EditAppDialogContentProps): ReactElement => {
	const {
		target,
		activateWhoHasAccessTab,
		onAfterSave,
		onBack: onBackFromProps,
		onClose,
		onIsBusyChanged
	} = props;

	const [appModel, setAppModel] = useState<EditAppModel<EditGenericAppViewModel> | null>(null);
	const [isAppLinkDialogOpen, setIsAppLinkDialogOpen] = useState<boolean>(false);
	const editAppComponentRef: RefObject<EditAppHandle | null> = useRef<EditAppHandle>(null);

	const [alertDialog, openAlertDialog] = useAlertDialog();
	const alertManager: AlertManager = useAlerts();

	const onBack: () => void = useStableImperativeCallback(onBackFromProps);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setAppModel(null);
					onIsBusyChanged(true);

					let model: EditAppModel<EditGenericAppViewModel>;
					switch (target.kind) {
						case "installed-app":
							model = await getEditAppModel(
								target.app
									.installedAppId,
								signal
							);

							break;
						case "new-environment-app":
							model = await getNewEnvironmentAppModel(
								target.appType,
								signal
							);

							break;
						case "new-app-from-main":
							model = await getNewAppFromMainModel(
								target.app
									.mainAppId,
								signal
							);

							break;
						default:
							never(target);
					}

					setAppModel(model);
					onIsBusyChanged(false);

				} catch (e) {
					onIsBusyChanged(false);

					if (isCancelledError(e)) {
						return;
					}

					const messageKey: LocalizationKey = (e instanceof HttpRequestError && e.statusCode === 403)
						? "_YouDoNotHavePermissionForThat"
						: "_GeneralErrorMessage";

					alertManager.showError(localize(messageKey));

					onBack();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			target,
			onIsBusyChanged,
			onBack
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			const { current: editAppComponentHandler } = editAppComponentRef;
			if (!editAppComponentHandler) {
				return;
			}

			onIsBusyChanged(true);

			try {
				const result: EditAppSavedResult = await editAppComponentHandler
					.save();

				const {
					id,
					kind,
					name
				} = result;

				onAfterSave(
					id,
					kind,
					name
				);

				onBack();
			} catch (e) {
				console.error(e);
			}

			onIsBusyChanged(false);
		},
		[
			onAfterSave,
			onBack,
			onIsBusyChanged
		]
	);

	const initialSettings = useMemo(
		(): EditAppSettings<EditGenericAppViewModel> | null => {
			if (!appModel) {
				return null;
			}

			return {
				target: target,
				model: appModel
			};
		},
		[appModel, target]
	);

	const showInfoDialogHandler = useMemo(
		(): (() => void) | undefined => {
			const appType: AppType = getAppType(target);
			const dialogInfo: InfoDialogDetails | null = getInfoDialogDetails(appType);

			if (!dialogInfo) {
				return undefined;
			}

			const {
				titleKey,
				descriptionKey,
				confirmTextKey
			} = dialogInfo;

			return () => {
				openAlertDialog({
					title: localize(titleKey),
					children: localize(descriptionKey),
					confirmText: localize(confirmTextKey),
					trackingId: "app-info-dialog"
				});
			};
		},
		[
			openAlertDialog,
			target
		]
	);

	return (
		<>
			{appModel && isAppInBeta(appModel.app.type) && (
				<FeaturedBanner kind="beta" />
			)}

			<Dialog.Header
				title={isNotNil(appModel)
					? <DialogTitle target={target} app={appModel.app} />
					: localize("_AppStore")
				}
				onClose={onClose}
				onBack={onBack}
				hasBorder
				onShowInfo={showInfoDialogHandler}
			/>

			<Dialog.Body
				goesEdgeToEdge
			>
				{initialSettings && (
					<ContentWithSidebar trackingId="edit-app">
						<ContentWithSidebar.Content>
							<EditApp
								initialSettings={initialSettings}
								initiallyActivateWhoHasAccessTab={activateWhoHasAccessTab}
								ref={editAppComponentRef}
							/>
						</ContentWithSidebar.Content>
					</ContentWithSidebar>
				)}
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					{target.kind === "installed-app" && appModel?.hasLinkNavigationSupport && (
						<Toolbar.Button
							kind="transparent-for-list"
							onClick={() => setIsAppLinkDialogOpen(true)}
							icon="link"
							text={localize("_CreateAppLink")}
							trackingId="create-link-button"
						/>
					)}

					<Toolbar.Button
						kind="secondary"
						onClick={onBack}
						text={localize("_Back")}
						trackingId="back-button"
						responsiveBehavior="hide"
					/>

					<Toolbar.Button
						kind="primary"
						onClick={saveHandler}
						text={localize("_Save")}
						trackingId="save-button"
					/>

					{onClose && (
						<Toolbar.Button
							kind="secondary"
							onClick={onClose}
							text={localize("_Close")}
							trackingId="close-button"
							responsiveBehavior="hide"
						/>
					)}
				</Toolbar>
			</Dialog.Footer>
			{alertDialog}
			{appModel && isNotNil(appModel.app.id) && isAppLinkDialogOpen && (
				<AppLinkDialog
					environmentAppId={appModel.app.id}
					onClose={() => setIsAppLinkDialogOpen(false)}
				/>
			)}
		</>
	);
};
EditAppDialogContent.displayName = "EditAppDialogContent";

type DialogTitleProps = {
	target: AppTarget;
	app: EditGenericAppViewModel;
};

const DialogTitle = (props: DialogTitleProps): ReactElement => {
	const {
		target,
		app
	} = props;

	const { key, name } = useMemo(
		(): { key: LocalizationKey; name: string; } => {
			switch (target.kind) {
				case "installed-app":
				case "new-app-from-main":
					return {
						key: "_EditAppTitle",
						name: app.name
					};
				case "new-environment-app":
					return {
						key: "_NewAppTitle",
						name: localize(
							ensureNotNil(
								appKindToNewAppNameKeyMap[app.type]
							)
						)
					};
				default:
					never(target);
			}
		},
		[
			app.name,
			app.type,
			target
		]
	);

	return (
		<>
			<FormattedText
				text={localize(key)}
				thingsToReplace={[
					{ what: "{{app}}", filler: name }
				]}
			/>

			<>
				<Space />
				-
				<Space />
				{getUserLicenseName(app.userLicense)}
			</>
		</>
	);
};
DialogTitle.displayName = "DialogTitle";

const appKindToNewAppNameKeyMap: Partial<{ [kind in AppType]: LocalizationKey; }> = {
	[AppType.Afas]: "_Afas",
	[AppType.Autotask]: "_Autotask",
	[AppType.AzureApp]: "_AzureApp",
	[AppType.CitrixXenApp]: "_CitrixXenApp",
	[AppType.ClientlessRdp]: "_ClientlessRDP",
	[AppType.Embed]: "_WebContent",
	[AppType.Liquit]: "_Recast",
	[AppType.LocalApp]: "_LocalApp",
	[AppType.Magister]: "_Magister",
	[AppType.NedapOns]: "_NedapOns",
	[AppType.Url]: "_Shortcut",
	[AppType.TopDesk]: "_TopDesk",
	[AppType.VilansKick]: "_VilansKick",
	[AppType.VmwareHorizonApp]: "_OmnissaHorizon",
	[AppType.WindowsVirtualDesktop]: "_AzureVirtualDesktop",
	[AppType.Zaaksysteem]: "_Zaaksysteem",
	[AppType.ZenyaDoc]: "_ZenyaDoc",
	[AppType.Zermelo]: "_Zermelo",
	[AppType.Integration]: "_IntegrationApp"
};

export interface ExistingInstalledAppTarget {
	kind: "installed-app";
	app: Omit<AppModel, "installedAppId"> & { installedAppId: number; };
}

export interface NewEnvironmentAppTarget {
	kind: "new-environment-app";
	appType: AppType;
}

export interface NewAppFromMainTarget {
	kind: "new-app-from-main";
	app: AppModel;
}

export type AppTarget =
	| ExistingInstalledAppTarget
	| NewEnvironmentAppTarget
	| NewAppFromMainTarget;

type InfoDialogDetails = {
	titleKey: LocalizationKey;
	descriptionKey: LocalizationKey;
	confirmTextKey: LocalizationKey;
};

function getAppType(
	target: AppTarget
): AppType {
	switch(target.kind) {
		case "installed-app":
		case "new-app-from-main":
			return target.app.appType;
		case "new-environment-app":
			return target.appType;
		default:
			never(target);
	}
}

function getInfoDialogDetails(
	appType: AppType
): InfoDialogDetails | null {
	switch(appType) {
		case AppType.LocalApp:
			return {
				titleKey: "_LocalApp",
				descriptionKey: "_LocalAppDialogInfo",
				confirmTextKey: "_Close"
			};
		default:
			return null;
	}
}